<template>
  <div class="acf-cf">
    <p v-if="!items.length" class="acf-cf__hint">
      {{ $t("sellFilterCustomEmpty") }}
    </p>

    <div
      v-for="(item, i) in items"
      :key="'acf-cf-' + i"
      class="acf-cf__row"
    >
      <v-combobox
        v-model="item.key"
        :items="keyItems"
        :search-input.sync="search"
        hide-selected
        :label="$t('additionalFields')"
        outlined
        dense
        hide-details
        :multiple="!!multiple"
        small-chips
        class="acf-cf__field"
      >
        <template v-slot:no-data>
          <v-list-item>
            <v-list-item-content>
              <v-list-item-title>
                {{ $t("toCreate") }}:
                <strong>{{ search }}</strong>. {{ $t("doEnter") }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
        <template v-slot:item="{ index, item: option }">
          {{ option }}
          <v-spacer />
          <v-btn icon x-small @click.stop.prevent="edit(index, option)">
            <v-icon small color="grey">mdi-pencil</v-icon>
          </v-btn>
        </template>
      </v-combobox>

      <v-text-field
        v-model="item.value"
        :label="$t('value')"
        outlined
        dense
        clearable
        hide-details
        class="acf-cf__field"
      />

      <button
        type="button"
        class="acf-cf__remove"
        :title="$t('remove')"
        @click="removeRow(item)"
      >
        <v-icon size="16">mdi-close</v-icon>
      </button>
    </div>

    <v-dialog
      v-model="dialog"
      :fullscreen="isMobile"
      :hide-overlay="isMobile"
      max-width="360"
      content-class="acf-wrap"
    >
      <div v-if="editing" class="acf-cf-edit">
        <div class="acf-cf-edit__title">{{ $t("edit") }}</div>
        <v-text-field
          v-model="editingText"
          outlined
          dense
          autofocus
          hide-details
          :loading="loading"
          :disabled="loading"
        />
        <div class="acf-cf-edit__actions">
          <button
            type="button"
            class="acf-cf-edit__btn acf-cf-edit__btn--primary"
            :disabled="loading"
            @click="updateItem"
          >
            {{ $t("edit") }}
          </button>
          <button
            type="button"
            class="acf-cf-edit__btn"
            @click="dialog = false"
          >
            {{ $t("cancel") }}
          </button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "InvoiceFilterCustomFields",
  props: {
    value: {
      default: null,
    },
    typeProp: {
      type: String,
      default: null,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    keyItems: [],
    items: [],
    loading: false,
    dialog: false,
    search: null,
    editing: null,
    editingText: null,
    oldText: null,
    timer: null,
  }),
  computed: {
    isMobile() {
      return this.$vuetify && this.$vuetify.breakpoint
        ? this.$vuetify.breakpoint.mobile
        : false;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.items = Array.isArray(val) ? val : [];
      },
    },
    items: {
      deep: true,
      handler(val) {
        this.$emit("input", val);
      },
    },
    search() {
      this.getItems();
    },
  },
  mounted() {
    this.getItems();
  },
  methods: {
    addRow() {
      if (!Array.isArray(this.items)) this.items = [];
      this.items.push({ key: null, value: null });
    },
    removeRow(item) {
      var i = this.items.indexOf(item);
      if (i > -1) this.items.splice(i, 1);
    },
    edit(index, item) {
      this.editing = item;
      this.editingText = item;
      this.oldText = item;
      this.dialog = true;
    },
    getItems() {
      var app = this;
      app.loading = true;
      clearTimeout(app.timer);
      app.timer = setTimeout(function () {
        app.$axios
          .get(app.$endpoint + "getdetail", {
            params: {
              select: app.typeProp,
              search: app.search,
            },
          })
          .then(function (res) {
            app.keyItems = res.data;
          })
          .catch(function (err) {
            if (typeof app.serverErrorHandeler === "function") {
              app.serverErrorHandeler(err);
            }
          })
          .finally(function () {
            app.loading = false;
          });
      }, 400);
    },
    updateItem() {
      var app = this;
      app.loading = true;
      clearTimeout(app.timer);
      app.timer = setTimeout(function () {
        app.$axios
          .get(app.$endpoint + "getdetail", {
            params: {
              select: "editCustomFields",
              type: app.typeProp,
              oldKey: app.oldText,
              key: app.editingText,
            },
          })
          .then(function () {
            app.getItems();
          })
          .catch(function (err) {
            if (typeof app.serverErrorHandeler === "function") {
              app.serverErrorHandeler(err);
            }
          })
          .finally(function () {
            app.loading = false;
            app.dialog = false;
            app.editingText = null;
            app.oldText = null;
          });
      }, 400);
    },
  },
};
</script>

<style scoped lang="scss">
.acf-cf {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.acf-cf__hint {
  margin: 0;
  font-size: 11.5px;
  color: var(--acf-muted, #64748b);
  line-height: 1.4;
}
.acf-cf__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) 28px;
  gap: 8px;
  align-items: center;
}
.acf-cf__field {
  min-width: 0;
}
.acf-cf__field ::v-deep .v-input {
  margin: 0 !important;
  padding: 0 !important;
  height: auto !important;
}
.acf-cf__field ::v-deep .v-text-field--outlined fieldset {
  border-radius: 6px;
}
.acf-cf__remove {
  width: 28px;
  height: 28px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--acf-muted, #64748b);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.acf-cf__remove:hover {
  color: #e11d48;
  background: rgba(225, 29, 72, 0.08);
}
.acf-cf-edit {
  padding: 16px;
  border-radius: 6px;
  background: var(--acf-surface, #fff);
}
.acf-cf-edit__title {
  margin-bottom: 12px;
  font-size: 13px;
  font-weight: 700;
}
.acf-cf-edit__actions {
  display: flex;
  gap: 8px;
  margin-top: 14px;
}
.acf-cf-edit__btn {
  height: 34px;
  padding: 0 12px;
  border: 1px solid var(--acf-border-2, #cbd5e1);
  border-radius: 6px;
  background: var(--acf-surface, #fff);
  color: var(--acf-text, #0f172a);
  font-size: 12.5px;
  cursor: pointer;
}
.acf-cf-edit__btn--primary {
  background: var(--pl-primary, #1976d2);
  border-color: var(--pl-primary, #1976d2);
  color: #fff;
  font-weight: 600;
}
@media (max-width: 799px) {
  .acf-cf__row {
    grid-template-columns: 1fr;
  }
  .acf-cf__remove {
    justify-self: start;
  }
}
</style>
